import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Paper, Typography, Box } from '@mui/material';
import { Bien, GroupeBien, Tiers } from '../../models/models';
import { ButtonType } from '../../constants/enums';
import { createBien, deleteBien, setSelectedBien } from '../../reduxStore/BienSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { useSnackbar } from '../../components/SnackbarProvider';
import MyButton from '../MyButton';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import GroupeBienAutoC from '../AutoComplete/GroupeBienAutoC';

interface BienFormProps {
  onCancel: () => void;
}

const BienForm: React.FC<BienFormProps> = ({ onCancel }) => {
  const { selectedBien } = useSelector((state: any) => state.BienSlice);
  const [resetKey, setResetKey] = useState(0); // Add this line
  
  const initialiseFormData = useMemo<Bien>(() => ({
    id: -1,
    nom: '',
    type: '',
    adresse: '',
    surface: null,
    description: '',
    groupe_bien_id: null,
    proprietaire_id:null,
    proprietaire:null,
    groupe_bien:null,
    loyer: 0,
    impot_foncier: 0,
  }), []);

  const [formData, setFormData] = useState<Bien>(initialiseFormData);

  useEffect(() => {
    if (selectedBien) {
      setFormData(selectedBien);
    } else {
      setFormData(initialiseFormData);
    }
  }, [selectedBien, initialiseFormData]);

  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleGroupeBienChange = (groupeBien: GroupeBien | null) => {
    setFormData((prev) => ({
      ...prev,
      groupe_bien_id: groupeBien ? groupeBien.id : null,
      groupe_bien: groupeBien, // Ensure it updates the object
    }));
  };
  const handleProprietaireChange = (proprietaire: Tiers | null) => {
    setFormData((prev) => ({
      ...prev,
      proprietaire_id: proprietaire ? proprietaire.id : null,
      proprietaire: proprietaire, // Ensure it updates the
    }));
  };

  const resetForm = () => {
    setFormData(initialiseFormData);
    setResetKey(prev => prev + 1); // Functional update
    dispatch(setSelectedBien(null));
  };

  const handleSubmit = async () => {
        try {
        await dispatch(createBien(formData)).unwrap();
        showSnackbar('Opération réussie!', 'success');
        dispatch(setSelectedBien(null));
        setFormData(initialiseFormData);
        resetForm();
        } catch (error) {
        showSnackbar(error as string || 'Échec de la création', 'error');
        }
   
  };

  const handleDelete = async () => {
    if (selectedBien && window.confirm('Êtes-vous sûr de vouloir supprimer ce bien ?')) {
      try {
        await dispatch(deleteBien(selectedBien.id ?? 0)).unwrap();
        showSnackbar('Bien supprimé!', 'success');
        dispatch(setSelectedBien(null));
        resetForm();
      } catch (error) {
        showSnackbar(error as string || 'Échec de la suppression', 'error');
      }
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      <Typography variant="h6" gutterBottom>
        {selectedBien ? 'Modifier un Bien' : 'Ajouter un Bien'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <TextField name="type" label="Type" value={formData.type} onChange={handleChange} fullWidth size="small" />
          <TextField name="nom" label="Nom" value={formData.nom} onChange={handleChange} fullWidth size="small" />
          <TextField name="adresse" label="Adresse" value={formData.adresse} onChange={handleChange} fullWidth size="small" />
          <TextField name="surface" label="Surface / m²" type="number" value={formData.surface ?? ''} onChange={handleChange} fullWidth size="small" />
          <TextField name="description" label="Description" value={formData.description ?? ''} onChange={handleChange} fullWidth size="small" />
          <TextField name="loyer" label="Loyer" type="number" value={formData.loyer ?? ''} onChange={handleChange} fullWidth size="small" />
          <TextField name="impot_foncier" label="Impôt Foncier" type="number" value={formData.impot_foncier ?? ''} onChange={handleChange} fullWidth size="small" />
          <FournisseurAutoC onFournisseurChange={handleProprietaireChange} 
                             key={`proprietaire${resetKey}`}
                             selectedFournisseur={formData.proprietaire}/>
          <GroupeBienAutoC key={`groupebien${resetKey}`}
                           onGroupeBienChange={handleGroupeBienChange}
                           selectedGroupeBien={formData.groupe_bien} />             
        </Box>
        <Box mt={2} display="flex" justifyContent="space-between">
          <div>
            <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit}>
              {selectedBien ? 'Modifier' : 'Ajouter'}
            </MyButton>
            <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={handleDelete}>
              Supprimer
            </MyButton>
            <MyButton actionType={ButtonType.CANCEL} variant="outlined" onClick={onCancel}>
              Annuler
            </MyButton>
          </div>
        </Box>
      </form>
    </Paper>
  );
};

export default BienForm;